<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { FetchArticleResponse } from "src/api/models/fetchArticleResponse";
import type { PatchArticleDTO } from "src/api/models/patchArticleDTO";
import type { PatchArticleOptionDTO } from "src/api/models/patchArticleOptionDTO";
import type { NestedArticleOptionResponse } from "src/api/models/nestedArticleOptionResponse";
import ArticleFormFields from "src/components/article-management/ArticleFormFields.vue";
import ArticleSupplierSection from "src/components/article-management/ArticleSupplierSection.vue";
import ArticleAlternativesSection from "src/components/article-management/ArticleAlternativesSection.vue";
import SharedAutocomplete from "src/components/shared/SharedAutocomplete.vue";
import { getFetchArticleByArticleOptionExternalId, getFetchArticleOption } from "src/api/queries/order-portal-bff";
import { formatCurrency } from "src/utils/currency";
import { calcDiscount, calcNetPrice, calcSalesPrice } from "src/utils/article-utils";
import { useToast } from "primevue/usetoast";
import type { CreateArticleOptionDTO } from "src/api/models";

interface Props {
  article: FetchArticleResponse | null;
  loading: boolean;
  operationInProgress: boolean;
  splitPercent: number;
  searchSupplierFn?: (query: string, page: number) => Promise<{ data: any[]; page: number; total_pages: number }>;
}

const { article, loading, operationInProgress, splitPercent, searchSupplierFn } = defineProps<Props>();

const wideLayout = computed(() => splitPercent <= 50);

const emit = defineEmits<{
  "update-article": [id: string, data: PatchArticleDTO];
  "update-option": [optionId: string, data: PatchArticleOptionDTO];
  "set-preferred-option": [articleId: string, optionId: string];
  "add-option": [articleId: string, data: CreateArticleOptionDTO];
  "remove-option": [articleId: string, optionId: string];
  close: [];
}>();

const toast = useToast();

// Article core form
interface FormData {
  name: string;
  description: string | null;
  measurement: string;
  minimum_margin: number;
  sale_price: number | null;
  internal_id: number | null;
}

const formData = ref<FormData>(getFormDefaults());
const originalFormData = ref<FormData>(getFormDefaults());

const isDirty = computed(() => JSON.stringify(formData.value) !== JSON.stringify(originalFormData.value));

function getFormDefaults(): FormData {
  return {
    name: "",
    description: null,
    measurement: "piece",
    minimum_margin: 0,
    sale_price: null,
    internal_id: null,
  };
}

function buildFormData(val: FetchArticleResponse): FormData {
  return {
    name: val.name ?? "",
    description: val.description ?? null,
    measurement: val.measurement ?? "piece",
    minimum_margin: val.minimum_margin ?? 0,
    sale_price: val.sale_price ?? null,
  };
}

watch(
  () => article,
  (val) => {
    if (val) {
      const snapshot = buildFormData(val);
      originalFormData.value = snapshot;
      formData.value = { ...snapshot };
    }
  },
  { immediate: true },
);

const preferredOption = computed(() => article?.preferred_article_option ?? null);
const isStockArticle = computed(() => !!preferredOption.value?.location);

const articleOptions = computed<NestedArticleOptionResponse[]>(() => article?.article_options ?? []);

function onFormUpdate(data: FormData) {
  formData.value = data;
}

function onSave() {
  if (!article?.id) return;
  if (!formData.value.name.trim()) {
    toast.add({ severity: "warn", summary: "Validation", detail: "Name is required", life: 3000 });
    return;
  }
  const patchData: PatchArticleDTO = {
    name: formData.value.name || null,
    description: formData.value.description,
    measurement: formData.value.measurement as PatchArticleDTO["measurement"],
    minimum_margin: formData.value.minimum_margin,
  };
  emit("update-article", article.id, patchData);
  originalFormData.value = { ...formData.value };
}

// Preferred option switch confirmation
const switchConfirmVisible = ref(false);
const pendingSwitchOptionId = ref<string | null>(null);

const pendingSwitchOption = computed(() => {
  if (!pendingSwitchOptionId.value) return null;
  return articleOptions.value.find((o) => o.id === pendingSwitchOptionId.value) ?? null;
});

const currentSalePrice = computed(() => {
  const netPrice = preferredOption.value?.purchase_net_price;
  if (netPrice == null || netPrice <= 0) return formData.value.sale_price ?? 0;
  return calcSalesPrice(netPrice, formData.value.minimum_margin);
});

const newSalePrice = computed(() => {
  const netPrice = pendingSwitchOption.value?.purchase_net_price;
  if (netPrice == null || netPrice <= 0) return 0;
  return calcSalesPrice(netPrice, formData.value.minimum_margin);
});

const salePriceWillDecrease = computed(() => currentSalePrice.value > 0 && newSalePrice.value < currentSalePrice.value);

function onSetPreferred(optionId: string) {
  if (!article?.id) return;
  pendingSwitchOptionId.value = optionId;
  switchConfirmVisible.value = true;
}

function onConfirmSwitch() {
  if (!article?.id || !pendingSwitchOptionId.value) return;
  emit("set-preferred-option", article.id, pendingSwitchOptionId.value);
  switchConfirmVisible.value = false;
  pendingSwitchOptionId.value = null;
}

function onCancelSwitch() {
  switchConfirmVisible.value = false;
  pendingSwitchOptionId.value = null;
}

function onRemoveOption(optionId: string) {
  if (!article?.id) return;
  emit("remove-option", article.id, optionId);
}

// Option dialog shared state
interface OptionFormData {
  brand_name: string;
  code: string;
  external_reference: string;
  location: string;
  purchase_gross_price: number;
  purchase_net_price: number;
}

const optionDialogVisible = ref(false);
const optionDialogMode = ref<"add" | "edit">("add");
const optionDialogLoading = ref(false);
const editingOptionId = ref<string | null>(null);
const selectedSupplier = ref<any>(null);
const optionForm = ref<OptionFormData>(getOptionFormDefaults());
const optionDiscount = ref(0);

const optionCalculatedNetPrice = computed(() =>
  calcNetPrice(optionForm.value.purchase_gross_price, optionDiscount.value),
);

const optionDialogHeader = computed(() =>
  optionDialogMode.value === "add" ? "Add Article Option" : "Edit Article Option",
);

function getOptionFormDefaults(): OptionFormData {
  return {
    brand_name: "",
    code: "",
    external_reference: "",
    location: "",
    purchase_gross_price: 0,
    purchase_net_price: 0,
  };
}

function resetOptionDialog() {
  optionForm.value = getOptionFormDefaults();
  optionDiscount.value = 0;
  selectedSupplier.value = null;
  editingOptionId.value = null;
  optionDialogLoading.value = false;
}

function populateOptionForm(opt: NestedArticleOptionResponse) {
  optionForm.value = {
    brand_name: opt.brand_name ?? "",
    code: opt.code ?? "",
    external_reference: opt.external_reference ?? "",
    location: opt.location ?? "",
    purchase_gross_price: opt.purchase_gross_price ?? 0,
    purchase_net_price: opt.purchase_net_price ?? 0,
  };
  optionDiscount.value = calcDiscount(opt.purchase_gross_price ?? 0, opt.purchase_net_price ?? 0);
  if (opt.supplier) {
    selectedSupplier.value = { id: opt.supplier.id, name: opt.supplier.name };
  }
}

function onOpenAddOption() {
  resetOptionDialog();
  optionDialogMode.value = "add";
  optionDialogVisible.value = true;
}

async function onOpenEditOption(optionId: string) {
  if (!article?.id) return;
  resetOptionDialog();
  optionDialogMode.value = "edit";
  editingOptionId.value = optionId;
  optionDialogVisible.value = true;
  optionDialogLoading.value = true;
  try {
    const articleOption = await getFetchArticleOption(optionId);

    if (!articleOption) {
      toast.add({ severity: "error", summary: "Error", detail: "Article option not found", life: 3000 });
      optionDialogVisible.value = false;
      return;
    }
    populateOptionForm(articleOption);
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to load article option", life: 3000 });
    optionDialogVisible.value = false;
  } finally {
    optionDialogLoading.value = false;
  }
}

function onOptionGrossPriceInput(e: { value: string | number | undefined }) {
  optionForm.value.purchase_gross_price = Number(e.value) || 0;
  optionForm.value.purchase_net_price = optionCalculatedNetPrice.value;
}

function onOptionDiscountInput(e: { value: string | number | undefined }) {
  optionDiscount.value = Number(e.value) || 0;
  optionForm.value.purchase_net_price = optionCalculatedNetPrice.value;
}

const optionFormValid = computed(() => {
  return optionForm.value.brand_name.trim() && selectedSupplier.value?.id;
});

function onOptionDialogSave() {
  if (!article?.id || !optionFormValid.value) return;

  if (optionDialogMode.value === "add") {
    emit("add-option", article.id, {
      external_reference: optionForm.value.external_reference || "",
      brand_name: optionForm.value.brand_name,
      code: optionForm.value.code || undefined,
      supplier_id: selectedSupplier.value.id,
      location: optionForm.value.location || undefined,
      purchase_gross_price: optionForm.value.purchase_gross_price,
      purchase_net_price: optionForm.value.purchase_net_price,
      purchase_discount: optionDiscount.value
    });
  } else if (editingOptionId.value) {
    const patchData: PatchArticleOptionDTO = {
      brand_name: optionForm.value.brand_name || null,
      code: optionForm.value.code || null,
      external_reference: optionForm.value.external_reference || null,
      location: optionForm.value.location || null,
      purchase_gross_price: optionForm.value.purchase_gross_price,
      purchase_net_price: optionForm.value.purchase_net_price,
      purchase_discount: optionDiscount.value,
    };
    emit("update-option", editingOptionId.value, patchData);
  }

  optionDialogVisible.value = false;
  resetOptionDialog();
}

function copyToClipboard(value: string | undefined | null) {
  if (!value) return;
  navigator.clipboard.writeText(value);
  toast.add({ severity: "info", summary: "Copied", detail: "Copied to clipboard", life: 2000 });
}

defineExpose({ isDirty });
</script>

<template>
  <div data-testid="article-detail-panel" class="h-full overflow-y-auto overflow-x-hidden px-2 relative">
    <template v-if="loading && !article">
      <div class="flex items-center gap-2 py-12 justify-center text-color-secondary">
        <i class="pi pi-spin pi-spinner" />
        Loading article details...
      </div>
    </template>

    <template v-else-if="!article">
      <div
        data-testid="article-detail-empty"
        class="flex flex-col items-center justify-center py-12 text-color-secondary gap-2"
      >
        <i class="pi pi-inbox text-4xl" />
        <span>Select an article to view details</span>
      </div>
    </template>

    <template v-else>
      <div
        v-if="loading"
        class="absolute inset-0 z-20 flex items-center justify-center bg-surface-0/60 dark:bg-surface-900/60"
      >
        <div class="flex items-center gap-2 text-color-secondary">
          <i class="pi pi-spin pi-spinner" />
          Loading...
        </div>
      </div>

      <div class="surface-ground pb-3 border-b surface-border" :class="isDirty ? 'border-l-4 border-l-primary' : ''">
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-2">
              <h2 data-testid="article-detail-name" class="text-lg font-semibold m-0 truncate">{{ article.name }}</h2>
              <Tag
                data-testid="article-detail-stock-indicator"
                :severity="isStockArticle ? 'success' : 'secondary'"
                :value="isStockArticle ? 'Stock' : 'Order'"
                class="text-xs shrink-0"
              />
            </div>
          </div>
          <Button
            data-testid="article-detail-close"
            icon="pi pi-times"
            severity="secondary"
            text
            rounded
            size="small"
            class="shrink-0"
            @click="emit('close')"
          />
        </div>

        <div class="flex items-center gap-2 mt-3">
          <Button
            data-testid="article-detail-save"
            label="Save"
            icon="pi pi-check"
            severity="success"
            size="small"
            :disabled="!isDirty || operationInProgress"
            @click="onSave"
          />
        </div>
      </div>

      <div class="mt-4 grid gap-4" :class="wideLayout ? 'grid-cols-2' : 'grid-cols-1'">
        <div>
          <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Core Fields</span>
          <div class="mt-2">
            <ArticleFormFields
              :model-value="formData"
              :purchase-net-price="preferredOption?.purchase_net_price ?? null"
              :disabled="operationInProgress"
              @update:model-value="onFormUpdate"
            />
          </div>
        </div>

        <div>
          <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide"
            >Preferred Article Option</span
          >
          <div class="mt-2">
            <ArticleSupplierSection :article="article" :disabled="operationInProgress" />
          </div>
        </div>
      </div>

      <div class="mt-4">
        <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide"
          >Article Options ({{ articleOptions.length }})</span
        >
        <div class="mt-2 overflow-x-auto">
          <ArticleAlternativesSection
            :options="articleOptions"
            :preferred-option-id="preferredOption?.id ?? null"
            :disabled="operationInProgress"
            @set-preferred="onSetPreferred"
            @add-option="onOpenAddOption"
            @remove-option="onRemoveOption"
            @edit-option="onOpenEditOption"
          />
        </div>
      </div>

      <Dialog
        data-testid="option-dialog"
        :visible="optionDialogVisible"
        modal
        :header="optionDialogHeader"
        :style="{ width: '500px' }"
        @update:visible="optionDialogVisible = $event"
      >
        <div v-if="optionDialogLoading" class="flex items-center gap-2 py-8 justify-center text-color-secondary">
          <i class="pi pi-spin pi-spinner" />
          Loading option...
        </div>
        <div v-else class="flex flex-col gap-4">
          <div class="grid grid-cols-2 gap-x-4 gap-y-3">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-color-secondary">Brand *</label>
              <InputText data-testid="option-dialog-brand" v-model="optionForm.brand_name" size="small" fluid />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-color-secondary">Code</label>
              <InputText data-testid="option-dialog-code" v-model="optionForm.code" size="small" fluid />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-color-secondary">External reference</label>
              <InputText
                data-testid="option-dialog-external-ref"
                v-model="optionForm.external_reference"
                size="small"
                fluid
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-color-secondary">Location</label>
              <InputText data-testid="option-dialog-location" v-model="optionForm.location" size="small" fluid />
            </div>
          </div>

          <div>
            <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Supplier</span>
            <div class="mt-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-color-secondary">Supplier *</label>
                <SharedAutocomplete
                  v-if="searchSupplierFn"
                  data-testid="option-dialog-supplier"
                  v-model="selectedSupplier"
                  :fetch-fn="searchSupplierFn"
                  option-label="name"
                  placeholder="Search supplier..."
                />
                <InputText
                  v-else
                  data-testid="option-dialog-supplier"
                  :model-value="selectedSupplier?.name ?? ''"
                  disabled
                  size="small"
                  placeholder="Supplier search not available"
                />
              </div>
            </div>
          </div>

          <div>
            <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Purchase pricing</span>
            <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-color-secondary">Gross price</label>
                <InputNumber
                  data-testid="option-dialog-gross-price"
                  v-model="optionForm.purchase_gross_price"
                  mode="currency"
                  currency="EUR"
                  locale="nl-NL"
                  :min="0"
                  size="small"
                  fluid
                  input-class="font-mono"
                  @input="onOptionGrossPriceInput"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-color-secondary">Discount</label>
                <InputNumber
                  data-testid="option-dialog-discount"
                  v-model="optionDiscount"
                  suffix="%"
                  :min="0"
                  :max="100"
                  size="small"
                  fluid
                  @input="onOptionDiscountInput"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-color-secondary">Net price</label>
                <div
                  data-testid="option-dialog-net-price"
                  class="font-mono text-sm px-2.5 py-1.5 border border-surface-300 dark:border-surface-600 rounded-md bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400"
                >
                  {{ formatCurrency(optionCalculatedNetPrice) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-2">
            <Button
              data-testid="option-dialog-cancel"
              label="Cancel"
              severity="secondary"
              text
              @click="optionDialogVisible = false"
            />
            <Button
              data-testid="option-dialog-save"
              :label="optionDialogMode === 'add' ? 'Add option' : 'Save'"
              icon="pi pi-check"
              severity="success"
              :disabled="operationInProgress || optionDialogLoading || !optionFormValid"
              @click="onOptionDialogSave"
            />
          </div>
        </template>
      </Dialog>

      <Dialog
        data-testid="switch-preferred-dialog"
        :visible="switchConfirmVisible"
        modal
        header="Switch preferred option"
        :style="{ width: '560px' }"
        @update:visible="onCancelSwitch"
      >
        <div v-if="pendingSwitchOption && preferredOption" class="flex flex-col gap-4 text-sm">
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-2 p-3 rounded-md surface-ground border surface-border">
              <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Current</span>
              <div class="font-semibold">{{ preferredOption.brand_name ?? "-" }}</div>
              <div class="text-color-secondary text-xs">{{ preferredOption.preferred_supplier?.name ?? "-" }}</div>
              <div class="flex flex-col gap-1 mt-1 pt-2 border-t surface-border">
                <div class="flex justify-between">
                  <span class="text-color-secondary">Net purchase</span>
                  <span class="font-mono">{{ formatCurrency(preferredOption.purchase_net_price ?? 0) }}</span>
                </div>
                <div class="flex justify-between font-semibold">
                  <span class="text-color-secondary">Sale price</span>
                  <span class="font-mono">{{ formatCurrency(currentSalePrice) }}</span>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 p-3 rounded-md border-2 border-primary/40 bg-primary/5">
              <span class="text-xs font-semibold text-primary uppercase tracking-wide">New</span>
              <div class="font-semibold">{{ pendingSwitchOption.brand_name ?? "-" }}</div>
              <div class="text-color-secondary text-xs">{{ pendingSwitchOption.preferred_supplier?.name ?? "-" }}</div>
              <div class="flex flex-col gap-1 mt-1 pt-2 border-t surface-border">
                <div class="flex justify-between">
                  <span class="text-color-secondary">Net purchase</span>
                  <span class="font-mono">{{ formatCurrency(pendingSwitchOption.purchase_net_price ?? 0) }}</span>
                </div>
                <div class="flex justify-between font-semibold">
                  <span class="text-color-secondary">Sale price</span>
                  <span class="font-mono">{{ formatCurrency(newSalePrice) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="salePriceWillDecrease"
            data-testid="switch-preferred-price-warning"
            class="flex items-start gap-2 px-3 py-2 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 text-xs"
          >
            <i class="pi pi-exclamation-triangle mt-0.5 shrink-0" />
            <span>Sale price stays at {{ formatCurrency(currentSalePrice) }} -- it never decreases.</span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-2">
            <Button
              data-testid="switch-preferred-cancel"
              label="Cancel"
              severity="secondary"
              text
              @click="onCancelSwitch"
            />
            <Button
              data-testid="switch-preferred-confirm"
              label="Switch preferred"
              icon="pi pi-arrow-right-arrow-left"
              severity="success"
              :disabled="operationInProgress"
              @click="onConfirmSwitch"
            />
          </div>
        </template>
      </Dialog>
    </template>
  </div>
</template>
